import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import { useCMS } from '../hooks/useCMS';
import { useToast } from '../hooks/use-toast';
import { trackPhoneClick } from '../utils/analytics';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactPublic = () => {
  const { get } = useCMS('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const { toast } = useToast();

  // Hent bedriftsinformasjon fra API
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/company-info`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Melding Sendt! ✅",
        description: "Vi tar kontakt med deg snart.",
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setLoading(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Formater åpningstider for visning
  const formatOpeningHours = () => {
    if (!companyInfo?.opening_hours) {
      return { weekdays: '07:00 - 16:00', saturday: 'Stengt', sunday: 'Stengt' };
    }
    
    const hours = companyInfo.opening_hours;
    const monday = hours.monday;
    const friday = hours.friday;
    const saturday = hours.saturday;
    const sunday = hours.sunday;
    
    // Sjekk om alle hverdager har samme tid
    const weekdayHours = monday?.is_open 
      ? `${monday.open} - ${friday?.close || monday.close}` 
      : 'Stengt';
    
    return {
      weekdays: weekdayHours,
      friday: friday?.is_open ? `${friday.open} - ${friday.close}` : 'Stengt',
      saturday: saturday?.is_open ? `${saturday.open} - ${saturday.close}` : 'Stengt',
      sunday: sunday?.is_open ? `${sunday.open} - ${sunday.close}` : 'Stengt'
    };
  };

  const openingHours = formatOpeningHours();
  const contact = companyInfo?.contact || {};
  const address = companyInfo?.address || {};
  const specialHours = companyInfo?.special_hours || {};

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-red-600/10 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-yellow-600/10 to-transparent blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Special Hours Notice */}
        {specialHours.is_active && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/50 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-yellow-400 mb-2">{specialHours.title || 'Avvikende åpningstider'}</h3>
                  <p className="text-gray-300 mb-3">{specialHours.message}</p>
                  {specialHours.hours && specialHours.hours.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                      {specialHours.hours.map((day, index) => (
                        <div key={index} className="bg-black/30 rounded-lg p-2 text-sm">
                          <span className="text-gray-400">{day.label || day.date}:</span>{' '}
                          <span className={day.is_open ? 'text-green-400' : 'text-red-400'}>
                            {day.is_open ? `${day.open} - ${day.close}` : 'Stengt'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-red-600/20 text-red-400 border-red-600/50 mb-4 px-4 py-1">
            {get('contact_badge', 'Kontakt Oss')}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-black mb-6">
            <span className="text-white">{get('contact_title_part1', 'Klar for å')}</span>{' '}
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">{get('contact_title_part2', 'hjelpe deg')}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {get('contact_subtitle', 'Ta kontakt med oss i dag for en profesjonell serviceavtale')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            {/* Quick Contact Cards */}
            <Card className="bg-gradient-to-br from-red-600/20 to-red-800/20 border-red-600/30 backdrop-blur-sm hover:border-red-600/50 transition-all duration-300 hover:transform hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">{get('contact_card_phone_title', 'Ring Oss')}</h3>
                    <p className="text-gray-400 text-sm mb-3">Man-Fre: {openingHours.weekdays}</p>
                    <a
                      href={`tel:${contact.phone || ''}`}
                      onClick={() => trackPhoneClick(contact.phone || '', 'contact_section')}
                      className="text-2xl font-bold text-red-400 hover:text-red-300 transition-colors duration-300"
                    >
                      {contact.phone_display || contact.phone || ''}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 backdrop-blur-sm hover:border-gray-600 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">{get('contact_card_email_title', 'Send E-post')}</h3>
                    <p className="text-gray-400 text-sm mb-3">{get('contact_card_email_subtitle', 'Vi svarer innen 24 timer')}</p>
                    <a
                      href={`mailto:${contact.email || ''}`}
                      className="text-lg font-semibold text-gray-300 hover:text-white transition-colors duration-300"
                    >
                      {contact.email || ''}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 backdrop-blur-sm hover:border-gray-600 transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">{get('contact_card_visit_title', 'Besøk Oss')}</h3>
                    <p className="text-gray-400 text-sm mb-3">{get('contact_card_visit_subtitle', 'Moderne verksted med god tilgjengelighet')}</p>
                    <p className="text-lg font-semibold text-gray-300">
                      {address.street ? `${address.street}, ${address.postal_code} ${address.city}` : 'Industrivegen 4, 2120 Sagstua'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-3">{get('contact_card_hours_title', 'Åpningstider')}</h3>
                    <div className="space-y-2 text-gray-300">
                      <div className="flex justify-between">
                        <span>Mandag - Torsdag:</span>
                        <span className="font-semibold">{openingHours.weekdays}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Fredag:</span>
                        <span className="font-semibold">{openingHours.friday}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Lørdag:</span>
                        <span className={`font-semibold ${openingHours.saturday === 'Stengt' ? 'text-gray-500' : ''}`}>
                          {openingHours.saturday}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Søndag:</span>
                        <span className={`font-semibold ${openingHours.sunday === 'Stengt' ? 'text-gray-500' : ''}`}>
                          {openingHours.sunday}
                        </span>
                      </div>
                    </div>
                    {companyInfo?.opening_hours_note && (
                      <p className="text-xs text-gray-500 mt-3 italic">{companyInfo.opening_hours_note}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700 backdrop-blur-sm">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-white mb-2">{get('contact_form_title', 'Send Oss En Melding')}</h3>
                <p className="text-gray-400 mb-6">{get('contact_form_subtitle', 'Fyll ut skjemaet så tar vi kontakt')}</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white">Navn *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-900 border-gray-700 text-white focus:border-red-600"
                      placeholder="Ditt fulle navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">E-post *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white focus:border-red-600"
                        placeholder="din@email.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-white">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-gray-900 border-gray-700 text-white focus:border-red-600"
                        placeholder="+47 123 45 678"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white">Melding *</Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      placeholder="Fortell oss hva du trenger hjelp med..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-lg py-6 rounded-xl transition-all duration-300"
                  >
                    {loading ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2 animate-spin" />
                        Sender...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Melding
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPublic;